import { Box } from '@material-ui/core';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const MainNav = ({
  textId,
  icon,
  linkTo,
  endAdornement,
  children,
}: {
  textId: string | undefined;
  icon: string;
  linkTo?: string;
  endAdornement?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const { palette, customBorderRadius } = useCustomTheme();

  const isNewApplication = icon == 'add';

  const linkStyle = {
    transition: 'background 1s',
    color: palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: customBorderRadius.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties;

  const linkActiveStyle = { ...linkStyle, background: palette.grey[100], transition: 'background 0.2s' };
  const linkInactiveStyle = { ...linkStyle, background: palette.grey[0] };

  const { pathname } = useLocation();
  const pathNodes = pathname.split('/').filter((e) => e);
  const isPathInCurrentPath = (path) => {
    if (!path) return false;
    if (pathname === path) return true;
    for (const node of pathNodes) {
      const cleanPath = path.replaceAll('/', '');
      if (node === cleanPath) {
        return true;
      }
    }
    return false;
  };

  const isExactPath = (path) => {
    if (!path) return false;
    if (path === pathname) return true;
    const cleanPath = path.replaceAll('/', '');
    return pathNodes.length === 1 && pathNodes[0] === cleanPath;
  };

  if (isNewApplication) {
    return (
      <StandardButton
        component={Link}
        to={linkTo ? `${linkTo}` : '#'}
        color={isExactPath(linkTo) ? 'secondary' : 'primary'}
        disabled={isExactPath(linkTo)}
        variant={isExactPath(linkTo) ? 'outlined' : 'contained'}
        size={'small'}
        style={{
          display: 'flex',
          width: 'min-content',
          padding: '4px 18px 4px 5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <Inner icon={icon} textId={textId} isNewApplication>
          {children}
        </Inner>
      </StandardButton>
    );
  }
  return (
    <Box style={isPathInCurrentPath(linkTo) ? linkActiveStyle : linkInactiveStyle}>
      <Link
        to={linkTo ? `${linkTo}` : '#'}
        style={{
          display: 'block',
          width: '100%',
          padding: '5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <Inner icon={icon} textId={textId}>
          {children}
        </Inner>
      </Link>
      {endAdornement ? <Box pr={1}>{endAdornement}</Box> : null}
    </Box>
  );
};

const Inner = ({
  icon,
  textId,
  isNewApplication = false,
  children,
}: {
  icon: string;
  textId: string | undefined;
  isNewApplication?: boolean;
  children?: React.ReactNode;
}) => {
  return (
    <Font variant={'subtitle2'} style={isNewApplication ? { fontWeight: 700 } : {}}>
      <i
        className={'material-icons-outlined mr-2'}
        style={{
          verticalAlign: '-6px',
          fontSize: '1.4rem',
        }}
      >
        {icon}
      </i>
      <Text tId={textId} />
      {children}
    </Font>
  );
};
